import { Maybe, ResponseErrorType } from '@/types/sharedTypes';
import { useQuery } from '@tanstack/react-query';
import {
  ADDING_ORG_ADMIN_NOTIFICATIONS_MAP,
  NewOrgAdminNotification,
} from '@/bundle/_Opco/ui/OrganizationDetails/component/AddingOrgAdminNotifications';
import { ReactNode, useEffect, useState } from 'react';
import { AddingOrgAdminStatusCodeType } from '@/api/v1/opco/organizations/getUserAddingStatusApi';
import { EMAIL } from '@/const/regExpPatterns';
import { UserEmailInput } from '@/bundle/shared/components/SearchUserEmailInput/ui/UserEmailInput';
import { getSearchUserId } from '@/bundle/shared/components/SearchUserEmailInput/SearchUserEmailInput';
import { searchUsers } from '../api/api';

type CreateOrgAdminInputType = {
  name?: string;
  email: string;
  assignmentStatus: AddingOrgAdminStatusCodeType;
  onSearch: (userId: null | string) => void;
  onSubmit: () => void;
  error?: string;
  apiError?: ResponseErrorType;
};

export const CreateOrgAdminInput = ({
  name = 'email',
  email,
  assignmentStatus,
  onSearch,
  onSubmit,
  error,
  apiError,
}: CreateOrgAdminInputType) => {
  const [notification, setNotification] = useState<Maybe<ReactNode>>(null);
  const shouldLoadUsers = EMAIL.test(email);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['search_user', email],
    queryFn: () => {
      return searchUsers(email);
    },
    enabled: false,
    gcTime: 0,
  });

  const getOrgAdminAutocompleteNotification = (statusCode: AddingOrgAdminStatusCodeType) => {
    if (!data?.body) return;

    const userId = !!getSearchUserId(data?.body?.results) && EMAIL.test(email);

    if (!userId) {
      return <NewOrgAdminNotification />;
    }

    return ADDING_ORG_ADMIN_NOTIFICATIONS_MAP[statusCode];
  };

  // TODO: think about how to catch only case when request is sent
  useEffect(() => {
    if (!data?.body) {
      setNotification(null);

      return;
    }

    const userId = getSearchUserId(data?.body?.results);
    const assignNotification = getOrgAdminAutocompleteNotification(assignmentStatus);

    setNotification(assignNotification);
    onSearch(userId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.body?.results, assignmentStatus]);

  const checkEmail = () => {
    onSubmit();

    if (shouldLoadUsers) {
      refetch();
    }
  };

  return (
    <UserEmailInput
      name={name}
      notification={notification}
      onSubmit={checkEmail}
      isLoading={isFetching}
      error={error}
      apiError={apiError}
    />
  );
};
